import { FaceIdRequester, GraphQLCameraDriverFaceClusterRequester } from '@cobliteam/api-lib';
import type { DriverFaceImageAssignRequest, DriverPhotosDetails, PageRequest, UpdateDriverFaceClusterRequest, UUID } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { keepPreviousData, type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create, windowedFiniteBatchScheduler } from '@yornaath/batshit';
import { QUERY_KEY as RUNNING_WITHOUT_DRIVER_QUERY_KEY } from '../../useEntity/useRunningWithoutDriverVehicles';

export type DriverFaceClustersPage = Awaited<ReturnType<GraphQLCameraDriverFaceClusterRequester['getDriverFaceClusterInfo']>>;
export type DriverFaceCluster = NonNullable<NonNullable<DriverFaceClustersPage['content']>[number]>;
export type DriverFaceClusterSampleImage = NonNullable<NonNullable<DriverFaceCluster['sampleImages']>[number]>;

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getFaceIdRequester = () => new FaceIdRequester({ host: env.apiHost });
const getGraphQLDriverFaceClusterRequester = () => new GraphQLCameraDriverFaceClusterRequester();

const batchFactory = (withoutSampleImageUrls: boolean) =>
  create({
    fetcher: async (driverIds: string[]) => {
      const page = await getGraphQLDriverFaceClusterRequester().getDriverFaceClusterInfo({
        page: 0,
        size: 10,
        driverIds,
        hasBeenNominated: true,
        withoutSampleImageUrls,
      });
      return page.content;
    },
    resolver: (items, query) => items?.find((cluster) => cluster?.driver?.id === query) ?? null,
    scheduler: windowedFiniteBatchScheduler({
      maxBatchSize: 10,
      windowMs: 600,
    }),
  });

const driverClustersBatcher = batchFactory(false);

const driverClustersBatcherWithoutImageSamples = batchFactory(true);

const QUERY_KEY = 'DRIVER-FACE-CLUSTER';

const useDriverFaceCluster = () => {
  const queryClient = useQueryClient();

  const invalidateInfinityDriverFaceClusters = () => {
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === QUERY_KEY && query.queryKey[1] === 'infinite',
    });
  };

  const useDriverFaceClusters = ({ page, size, sort }: PageRequest, driverIds?: UUID[], hasBeenNominated?: boolean, withoutSampleImageUrls?: boolean) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<DriverFaceClustersPage>({
      queryKey: [QUERY_KEY, page, size, sort, driverIds, hasBeenNominated, withoutSampleImageUrls],
      queryFn: () =>
        getGraphQLDriverFaceClusterRequester().getDriverFaceClusterInfo({
          page,
          size,
          sort,
          driverIds,
          hasBeenNominated,
          withoutSampleImageUrls,
        }),
      retry: false,
      enabled: true,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: 300000,
    });
  };

  const useDriverFaceClusterQuery = (driverId: string | undefined, options: Partial<UseQueryOptions<DriverFaceCluster | null>>, withoutSampleImageUrls?: boolean) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<DriverFaceCluster | null>({
      queryKey: [QUERY_KEY, 'driver', driverId, withoutSampleImageUrls],
      queryFn: async () => {
        if (!driverId) return null;
        if (withoutSampleImageUrls) {
          return driverClustersBatcherWithoutImageSamples.fetch(driverId);
        }

        return driverClustersBatcher.fetch(driverId);
      },
      staleTime: 300_000,
      ...options,
    });
  };

  return {
    useDriverFaceClusters,
    useDriverFaceClusterQuery,
    invalidateInfinityDriverFaceClusters,
  };
};

const useGetDriverFaceCluster = (driverId: string | undefined, options: Partial<UseQueryOptions<DriverFaceCluster | null>>) => {
  return useQuery<DriverFaceCluster | null>({
    queryKey: [QUERY_KEY, 'driver', driverId],
    queryFn: async () => {
      if (!driverId) return null;
      return driverClustersBatcher.fetch(driverId);
    },
    enabled: !!driverId,
    staleTime: 300_000,
    ...options,
  });
};

const useGetDriverFaceClusters = (
  driverIds?: UUID[],
  pageRequest?: PageRequest,
  hasBeenNominated?: boolean,
  vehicleIds?: UUID[],
  fromTimestamp?: string,
  toTimestamp?: string,
  samplesPerCluster?: number,
  options?: { enabled?: boolean }
) => {
  const { page, size, sort } = pageRequest ?? {};

  return useQuery({
    queryKey: [QUERY_KEY, page, size, sort, driverIds, hasBeenNominated, vehicleIds, fromTimestamp, toTimestamp, samplesPerCluster],
    queryFn: () =>
      getGraphQLDriverFaceClusterRequester().getDriverFaceClusterInfo({
        page,
        size,
        sort,
        driverIds,
        hasBeenNominated,
        vehicleIds,
        fromTimestamp,
        toTimestamp,
        samplesPerCluster,
      }),
    retry: false,
    enabled: options?.enabled ?? true,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 300000,
    placeholderData: keepPreviousData,
  });
};

const useAssignDriverFaceClusters = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, unknown, { assignClusters: UpdateDriverFaceClusterRequest[] }>({
    mutationFn: (request) => getGraphQLDriverFaceClusterRequester().assignFaceCluster(request.assignClusters),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY],
      });
      // Naming a cluster removes it from the "running without driver" view; refetch so a vehicle whose
      // last unnamed face was just assigned drops off the list.
      queryClient.invalidateQueries({
        queryKey: [RUNNING_WITHOUT_DRIVER_QUERY_KEY],
      });
    },
  });
};

const useRejectClusterSuggestion = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, unknown, { clusterId: UUID }>({
    mutationFn: async ({ clusterId }) => {
      // The mutation resolves to `false` on failure instead of throwing, so we
      // surface it as an error to drive the caller's onError/error toast.
      const ok = await getGraphQLDriverFaceClusterRequester().rejectClusterSuggestion(clusterId);
      if (!ok) throw new Error('rejectClusterSuggestion returned false');
      return ok;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY],
      });
    },
  });
};

const useDeleteDriverFaceCluster = () => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, { id: UUID }>({
    mutationFn: (request) => getFaceIdRequester().deleteDriverFaceCluster(request.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY],
      });

      Alert.success('Motorista deletado com sucesso!');
    },
    onError: () => {
      Alert.error('Erro ao deletar motorista. Tente novamente.');
    },
  });
};

const useAssignDriverFaceImage = () => {
  const queryClient = useQueryClient();

  return useMutation<void, any, DriverFaceImageAssignRequest[]>({
    mutationFn: async (assignments) => {
      return getFaceIdRequester().assignFaceImages(assignments);
    },
    onSuccess: (_, assignments) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY],
      });

      const uniqueDriverIds = [...new Set(assignments.map((assignment) => assignment.driverId))];
      const queryKey = uniqueDriverIds.length > 1 ? ['DRIVERS'] : [QUERY_KEY, 'driver', uniqueDriverIds[0]];
      queryClient.invalidateQueries({ queryKey });
    },
    retry: false,
  });
};

export default useDriverFaceCluster;
export { useAssignDriverFaceClusters, useAssignDriverFaceImage, useDeleteDriverFaceCluster, useGetDriverFaceCluster, useGetDriverFaceClusters, useRejectClusterSuggestion };

const useDriverClusterDetails = (clusterId: string | undefined) => {
  return useQuery<DriverPhotosDetails | null>({
    queryKey: [QUERY_KEY, 'cluster-details', clusterId],
    queryFn: async () => {
      if (!clusterId) return null;
      return getFaceIdRequester().getClusterDetails(clusterId);
    },
    staleTime: 300_000,
    enabled: !!clusterId,
  });
};

export { useDriverClusterDetails };
